import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import { ArrowLeft, Mail, PenLine, Save, Star, Users } from 'lucide-react';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { Button, buttonVariants, Label, Switch, TextInput } from '../../components/ui';
import Card from '../../components/ui/Card';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateTime } from '../../config/appConfig';
import { cn } from '../../lib/utils';
import type { SlateEmailEditorRef } from '../../components/editor/SlateEmailEditor';
import { prepareSignatureHtmlForEditor, preserveSignatureSpaces } from '../emails/utils/signatureUtils';
import {
  useSignature,
  useUsersForSignatureAssign,
  useAccountsForSignatureAssign,
  createSignature,
  updateSignature,
  formatUserLabel,
  formatAccountLabel,
  useCanManageEmailSignatures,
  SIGNATURES_MANAGE_KEY,
  SIGNATURES_LIST_KEY,
} from './useData';
import { moduleConfig, canUserEditSignature, canUserSelfEditSignature } from './config';
import SignatureBodyEditor from './SignatureBodyEditor';
import SignaturePreview from './SignaturePreview';

const FORM_ID = 'email-signature-form';

type SignatureFormTab = 'details' | 'assignment' | 'content';

interface SignatureFormNavProps {
  items: Array<{
    id: SignatureFormTab;
    label: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
  }>;
  activeTab: SignatureFormTab;
  onChange: (tab: SignatureFormTab) => void;
}

const SignatureFormNav: React.FC<SignatureFormNavProps> = ({ items, activeTab, onChange }) => {
  const { colors } = useTheme();

  const renderButton = (
    item: SignatureFormNavProps['items'][number],
    compact: boolean,
  ) => {
    const Icon = item.icon;
    const active = activeTab === item.id;
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => onChange(item.id)}
        className={cn(
          'text-left transition-all duration-200',
          compact
            ? cn(
                'inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium',
                active
                  ? cn(colors.primary, colors.primaryText, 'border-transparent shadow-sm')
                  : cn(colors.border, colors.textSecondary, colors.surface, colors.interactiveHover),
              )
            : cn(
                'flex w-full items-center gap-3 rounded-xl px-3 py-3',
                active
                  ? cn(colors.primary, colors.primaryText, 'shadow-sm')
                  : cn(colors.textSecondary, colors.interactiveHover),
              ),
        )}
      >
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded-lg',
            compact ? 'h-4 w-4' : 'h-9 w-9',
            !compact && (active ? 'bg-white/15' : cn(colors.surface, 'border', colors.border)),
          )}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        {compact ? (
          <span>{item.label}</span>
        ) : (
          <span className="min-w-0 flex-1">
            <span className={cn('block text-sm font-medium', active ? colors.primaryText : colors.text)}>
              {item.label}
            </span>
            <span className={cn('mt-0.5 block truncate text-xs', active ? 'opacity-90' : colors.textMuted)}>
              {item.description}
            </span>
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      <div className={cn('flex gap-2 overflow-x-auto border-b p-3 lg:hidden', colors.border)}>
        {items.map((item) => renderButton(item, true))}
      </div>
      <nav className={cn('hidden lg:flex lg:w-56 lg:shrink-0 lg:flex-col lg:gap-1 lg:border-r lg:p-3', colors.border)}>
        {items.map((item) => renderButton(item, false))}
      </nav>
    </>
  );
};

interface SectionCardProps {
  title: string;
  description?: string;
  icon: React.ReactNode;
  badge?: React.ReactNode;
  children: React.ReactNode;
}

const SectionCard: React.FC<SectionCardProps> = ({ title, description, icon, badge, children }) => {
  const { colors } = useTheme();
  return (
    <Card className="overflow-hidden">
      <div className={cn('flex items-start gap-3 border-b px-5 py-4', colors.border, colors.surfaceHover)}>
        <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', colors.info)}>
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className={cn('text-sm font-semibold', colors.text)}>{title}</h3>
            {badge}
          </div>
          {description ? <p className={cn('mt-0.5 text-xs', colors.textMuted)}>{description}</p> : null}
        </div>
      </div>
      <div className="px-5 py-4">{children}</div>
    </Card>
  );
};

const Form: React.FC = () => {
  const { colors } = useTheme();
  const { user: currentUser, permissionsLoaded } = useAuth();
  const canManage = useCanManageEmailSignatures();
  const navigate = useNavigate();
  const { mutate: globalMutate } = useSWRConfig();
  const { id } = useParams<{ id: string }>();
  const isEdit = id != null;
  const sigId = id ? parseInt(id, 10) : null;
  const { data: signature, isLoading: loadingSig } = useSignature(sigId);
  const { data: users, isLoading: loadingUsers } = useUsersForSignatureAssign(canManage);
  const { data: accounts, isLoading: loadingAccounts } = useAccountsForSignatureAssign(canManage);

  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState<number[]>([]);
  const [htmlMode, setHtmlMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editInitialized, setEditInitialized] = useState(false);
  const [activeTab, setActiveTab] = useState<SignatureFormTab>('details');
  const editorRef = useRef<SlateEmailEditorRef>(null);
  const bodyKeyRef = useRef(0);

  useEffect(() => {
    if (!isEdit) return;
    setEditInitialized(false);
    setName('');
    setBody('');
    setIsDefault(false);
    setSelectedUserIds([]);
    setSelectedAccountIds([]);
  }, [sigId, isEdit]);

  useEffect(() => {
    if (!isEdit || !signature || sigId == null || signature.id !== sigId) return;
    setName(signature.name);
    setBody(prepareSignatureHtmlForEditor(signature.body || ''));
    setIsDefault(signature.isDefault ?? false);
    setSelectedUserIds(signature.assignedUserIds ?? []);
    setSelectedAccountIds(signature.assignedAccountIds ?? []);
    bodyKeyRef.current += 1;
    setEditInitialized(true);
  }, [isEdit, signature, sigId]);

  useEffect(() => {
    if (!isEdit && canManage && currentUser && selectedUserIds.length === 0) {
      setSelectedUserIds([currentUser.id]);
    }
  }, [isEdit, canManage, currentUser, selectedUserIds.length]);

  useEffect(() => {
    if (!isEdit && canManage && accounts.length > 0 && selectedAccountIds.length === 0) {
      setSelectedAccountIds(accounts.map((a) => a.id));
    }
  }, [isEdit, canManage, accounts, selectedAccountIds.length]);

  useEffect(() => {
    if (sigId != null) globalMutate(`/emails/signatures/${sigId}`);
  }, [sigId, globalMutate]);

  const isOwnerEdit =
    isEdit && currentUser != null && signature?.createdByUserId === currentUser.id;
  const isSelfEdit = isEdit && !canManage && !isOwnerEdit;
  const canEditThisSignature =
    signature != null && canUserEditSignature(signature, currentUser?.id);

  useEffect(() => {
    if (!permissionsLoaded) return;
    if (!isEdit && !canManage) {
      navigate(moduleConfig.module.route.list, { replace: true });
    }
  }, [permissionsLoaded, isEdit, canManage, navigate]);

  useEffect(() => {
    if (!isEdit || !signature || !permissionsLoaded) return;
    if (!canUserEditSignature(signature, currentUser?.id)) {
      navigate(moduleConfig.module.route.list, { replace: true });
    }
  }, [isEdit, signature, permissionsLoaded, navigate, currentUser?.id]);

  const handleHtmlModeChange = (nextHtmlMode: boolean) => {
    if (nextHtmlMode && !htmlMode) {
      const latest = editorRef.current?.getHtml?.() ?? body;
      setBody(latest);
    } else if (!nextHtmlMode && htmlMode) {
      // Plain-text returns become spaces if loaded into Slate as raw text — structure first.
      setBody(prepareSignatureHtmlForEditor(body));
      bodyKeyRef.current += 1;
    }
    setHtmlMode(nextHtmlMode);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required.');
      return;
    }
    if (!isSelfEdit) {
      if (selectedUserIds.length === 0) {
        setError('Select at least one user who can use this signature.');
        return;
      }
      if (selectedAccountIds.length === 0) {
        setError('Select at least one email account for this signature.');
        return;
      }
    }
    setSaving(true);
    try {
      const rawBody = htmlMode ? body : (editorRef.current?.getHtml?.() ?? body);
      // Keep Enter/returns as block structure; preserve multi-space layout for email clients.
      const bodyToSave = preserveSignatureSpaces(prepareSignatureHtmlForEditor(rawBody));

      if (isEdit && sigId != null) {
        await updateSignature(
          sigId,
          isSelfEdit
            ? { name: trimmedName, body: bodyToSave, isDefault }
            : {
                name: trimmedName,
                body: bodyToSave,
                isDefault,
                userIds: selectedUserIds,
                accountIds: selectedAccountIds,
              },
        );
        await Promise.all([
          globalMutate(SIGNATURES_MANAGE_KEY),
          globalMutate(SIGNATURES_LIST_KEY),
          globalMutate('/emails/signatures'),
          globalMutate(`/emails/signatures/${sigId}`),
        ]);
        navigate(moduleConfig.module.route.list);
      } else {
        await createSignature({
          name: trimmedName,
          body: bodyToSave,
          isDefault,
          userIds: selectedUserIds,
          accountIds: selectedAccountIds,
        });
        await Promise.all([
          globalMutate(SIGNATURES_MANAGE_KEY),
          globalMutate(SIGNATURES_LIST_KEY),
          globalMutate('/emails/signatures'),
        ]);
        navigate(moduleConfig.module.route.list);
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Failed to save signature.');
    } finally {
      setSaving(false);
    }
  };

  const assignedCountLabel = `${selectedUserIds.length} user${selectedUserIds.length !== 1 ? 's' : ''}`;
  const assignedAccountsLabel = `${selectedAccountIds.length} account${selectedAccountIds.length !== 1 ? 's' : ''}`;

  const formNav = useMemo(() => {
    const items: SignatureFormNavProps['items'] = [
      { id: 'details', label: 'Details', description: 'Name & default', icon: PenLine },
    ];
    if (canManage && !isSelfEdit) {
      items.push({
        id: 'assignment',
        label: 'Assignment',
        description: 'Users & accounts',
        icon: Users,
      });
    }
    items.push({ id: 'content', label: 'Content', description: 'Body & preview', icon: Mail });
    return items;
  }, [canManage, isSelfEdit]);

  const currentTab = formNav.find((t) => t.id === activeTab) ?? formNav[0];

  useEffect(() => {
    if (!formNav.some((t) => t.id === activeTab)) {
      setActiveTab(formNav[0]?.id ?? 'details');
    }
  }, [activeTab, formNav]);

  if (isEdit && (loadingSig || (signature != null && !editInitialized))) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <LoadingSpinner size="large" message="Loading signature…" />
      </div>
    );
  }

  if (isEdit && signature == null) {
    return (
      <div className="max-w-[90rem] mx-auto px-4 py-6 sm:px-6">
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          Signature not found or you may not have access.
        </div>
        <div className="mt-4">
          <Link
            to={moduleConfig.module.route.list}
            className={cn(
              buttonVariants({ variant: 'outline' }),
              colors.surface,
              colors.text,
              colors.border,
            )}
          >
            Back to signatures
          </Link>
        </div>
      </div>
    );
  }

  if (isEdit && signature != null && editInitialized && !canEditThisSignature) {
    return (
      <div className="max-w-[90rem] mx-auto px-4 py-6 sm:px-6">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
          This signature is shared with multiple users and can only be edited by its owner.
        </div>
        <div className="mt-4">
          <Link
            to={moduleConfig.module.route.list}
            className={cn(
              buttonVariants({ variant: 'outline' }),
              colors.surface,
              colors.text,
              colors.border,
            )}
          >
            Back to signatures
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('h-full overflow-y-auto', colors.background)}>
      <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
        <Link
          to={moduleConfig.module.route.list}
          className={cn(
            'inline-flex items-center gap-1.5 text-sm font-medium transition-colors',
            colors.textMuted,
            colors.interactiveHover,
          )}
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to signatures
        </Link>

        <div className={cn('rounded-2xl border px-5 py-5 sm:px-6', colors.border, colors.surface)}>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-4 min-w-0">
              <span className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-xl', colors.info)}>
                <PenLine className="h-5 w-5" aria-hidden />
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className={cn('text-lg font-semibold tracking-tight', colors.text)}>
                    {isEdit ? (isSelfEdit ? 'Edit my signature' : 'Edit signature') : 'New signature'}
                  </h1>
                  {isDefault && (
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold',
                        'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-300',
                      )}
                    >
                      <Star className="h-3 w-3 fill-current" aria-hidden />
                      Default
                    </span>
                  )}
                </div>
                <p className={cn('mt-1 text-sm leading-relaxed', colors.textMuted)}>
                  {isSelfEdit
                    ? 'Update your signature content and default preference.'
                    : 'Assign users and email accounts, then design the HTML signature body.'}
                </p>
                {isEdit && signature?.updatedAt && (
                  <p className={cn('mt-1 text-xs', colors.textMuted)}>
                    Last updated {formatDateTime(signature.updatedAt)}
                  </p>
                )}
              </div>
            </div>
            {isEdit && signature?.ownerName && (
              <div
                className={cn(
                  'inline-flex shrink-0 items-center gap-2 rounded-lg border px-3 py-2 text-sm',
                  colors.border,
                  colors.surfaceHover,
                )}
              >
                <span className={cn('text-xs', colors.textMuted)}>Created by</span>
                <span className={cn('font-medium truncate', colors.text)}>{signature.ownerName}</span>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
            {error}
          </div>
        )}

        <form id={FORM_ID} onSubmit={handleSubmit}>
          <Card className="overflow-hidden">
            <div className="flex min-h-0 flex-col lg:min-h-[36rem] lg:flex-row">
              <SignatureFormNav items={formNav} activeTab={activeTab} onChange={setActiveTab} />

              <div className="flex min-w-0 flex-1 flex-col">
                {currentTab ? (
                  <div className={cn('hidden border-b px-6 py-4 lg:block', colors.border, colors.surfaceHover)}>
                    <h2 className={cn('text-base font-semibold', colors.text)}>{currentTab.label}</h2>
                    <p className={cn('mt-0.5 text-sm', colors.textMuted)}>{currentTab.description}</p>
                  </div>
                ) : null}

                <div className="flex-1 overflow-y-auto dashboard-scrollbar p-4 sm:p-6">
                  {activeTab === 'details' && (
                    <div className="max-w-2xl space-y-6">
                      <SectionCard
                        title="Signature details"
                        description="A short label to identify this signature in the list and compose menu"
                        icon={<PenLine className="h-4 w-4" aria-hidden />}
                      >
                        <div className="space-y-5">
                          <div>
                            <Label htmlFor="sig-name" required>
                              Signature name
                            </Label>
                            <TextInput
                              id="sig-name"
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              placeholder="e.g. Work, Sales team"
                              maxLength={255}
                              className="mt-1"
                            />
                          </div>
                          <div
                            className={cn(
                              'flex items-center justify-between gap-4 rounded-xl border px-4 py-3',
                              colors.border,
                              colors.surface,
                            )}
                          >
                            <div className="min-w-0">
                              <p className={cn('text-sm font-medium', colors.text)}>Default signature</p>
                              <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
                                Automatically insert when composing from an assigned account
                              </p>
                            </div>
                            <Switch
                              checked={isDefault}
                              onChange={(e) => setIsDefault(e.target.checked)}
                              aria-label="Use as default signature"
                            />
                          </div>
                        </div>
                      </SectionCard>
                    </div>
                  )}

                  {activeTab === 'assignment' && canManage && !isSelfEdit && (
                    <div className="space-y-6">
                      <SectionCard
                        title="Assigned users"
                        description="Selected users can insert this signature when composing from an assigned account"
                        icon={<Users className="h-4 w-4" aria-hidden />}
                        badge={
                          <span className={cn('text-xs font-medium tabular-nums', colors.textMuted)}>
                            {assignedCountLabel}
                          </span>
                        }
                      >
                        {loadingUsers ? (
                          <p className={cn('text-sm py-2', colors.textMuted)}>Loading users…</p>
                        ) : (
                          <div className={cn('rounded-lg border overflow-hidden max-h-72 overflow-y-auto', colors.border)}>
                            {users.map((u) => {
                              const selected = selectedUserIds.includes(u.id);
                              const userLabel = formatUserLabel(u);
                              return (
                                <div
                                  key={u.id}
                                  className={cn(
                                    'flex items-center justify-between gap-3 px-4 py-3 border-b last:border-b-0',
                                    colors.border,
                                    selected && 'bg-indigo-50/50 dark:bg-indigo-900/20',
                                  )}
                                >
                                  <span className={cn('text-sm font-medium truncate min-w-0', colors.text)}>
                                    {userLabel}
                                  </span>
                                  <Switch
                                    checked={selected}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedUserIds((prev) =>
                                          prev.includes(u.id) ? prev : [...prev, u.id],
                                        );
                                      } else {
                                        setSelectedUserIds((prev) => prev.filter((uid) => uid !== u.id));
                                      }
                                    }}
                                    aria-label={`Assign signature to ${userLabel}`}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </SectionCard>

                      <SectionCard
                        title="Email accounts"
                        description="Signature appears in compose only when sending from a selected account"
                        icon={<Mail className="h-4 w-4" aria-hidden />}
                        badge={
                          <span className={cn('text-xs font-medium tabular-nums', colors.textMuted)}>
                            {assignedAccountsLabel}
                          </span>
                        }
                      >
                        {loadingAccounts ? (
                          <p className={cn('text-sm py-2', colors.textMuted)}>Loading accounts…</p>
                        ) : accounts.length === 0 ? (
                          <p className={cn('text-sm py-2', colors.textMuted)}>No email accounts configured.</p>
                        ) : (
                          <div className={cn('rounded-lg border overflow-hidden max-h-72 overflow-y-auto', colors.border)}>
                            {accounts.map((acc) => {
                              const selected = selectedAccountIds.includes(acc.id);
                              const accountLabel = acc.label || formatAccountLabel(acc);
                              return (
                                <div
                                  key={acc.id}
                                  className={cn(
                                    'flex items-center justify-between gap-3 px-4 py-3 border-b last:border-b-0',
                                    colors.border,
                                    selected && 'bg-indigo-50/50 dark:bg-indigo-900/20',
                                  )}
                                >
                                  <div className="min-w-0">
                                    <span className={cn('text-sm font-medium truncate block', colors.text)}>
                                      {accountLabel}
                                    </span>
                                    {acc.displayName?.trim() && acc.emailAddress && (
                                      <span className={cn('text-xs truncate block', colors.textMuted)}>
                                        {acc.emailAddress}
                                      </span>
                                    )}
                                  </div>
                                  <Switch
                                    checked={selected}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedAccountIds((prev) =>
                                          prev.includes(acc.id) ? prev : [...prev, acc.id],
                                        );
                                      } else {
                                        setSelectedAccountIds((prev) => prev.filter((aid) => aid !== acc.id));
                                      }
                                    }}
                                    aria-label={`Use signature for ${accountLabel}`}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </SectionCard>
                    </div>
                  )}

                  {activeTab === 'content' && (
                    <div className="flex flex-col gap-6">
                      <SignatureBodyEditor
                        body={body}
                        onChange={setBody}
                        htmlMode={htmlMode}
                        onHtmlModeChange={handleHtmlModeChange}
                        placeholder="Best regards, Your Name — add formatting, links, or a logo table."
                        contentKey={isEdit ? `edit-${sigId}-${bodyKeyRef.current}` : 'new'}
                        colors={colors as unknown as Record<string, string>}
                        editorRef={editorRef}
                      />
                      <SignaturePreview html={body} />
                    </div>
                  )}
                </div>

                <div
                  className={cn(
                    'flex shrink-0 items-center justify-end gap-3 border-t px-4 py-4 sm:px-6',
                    colors.border,
                    colors.surfaceHover,
                  )}
                >
                  <Link
                    to={moduleConfig.module.route.list}
                    className={cn(
                      buttonVariants({ variant: 'outline' }),
                      colors.surface,
                      colors.text,
                      colors.border,
                    )}
                  >
                    Cancel
                  </Link>
                  <Button type="submit" disabled={saving} loading={saving} className="gap-2">
                    <Save className="h-4 w-4" aria-hidden />
                    {isEdit ? 'Save changes' : 'Create signature'}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </form>
      </div>
    </div>
  );
};

export default Form;
